/**
 * Capacitor deep-link handler.
 *
 * When Android opens an https://istudypro.net/* URL inside the APK
 * (because of the AndroidManifest <intent-filter> + assetlinks.json),
 * Capacitor emits an `appUrlOpen` event. We translate it into a
 * react-router navigation so e.g. the Google OAuth callback URL
 * `https://istudypro.net/auth/google/callback?code=...` lands on
 * the existing `<GoogleOAuthCallback />` page INSIDE the app.
 *
 * No-op on the web — `Capacitor` is undefined.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const isCapacitorNative = () => {
  try {
    return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform());
  } catch {
    return false;
  }
};

export default function CapacitorDeepLinkHandler() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!isCapacitorNative()) return;

    let removeListener;

    (async () => {
      try {
        // Lazy import so the web build doesn't pull the @capacitor/app
        // plugin (it ships with the native shell only).
        const { App } = await import('@capacitor/app');
        const handle = await App.addListener('appUrlOpen', (event) => {
          try {
            const url = new URL(event.url);
            // Translate the incoming https URL into an in-app route. We
            // preserve the search + hash so OAuth/magic-link/payment
            // callbacks still see their query params.
            const target = `${url.pathname}${url.search}${url.hash}`;
            if (target && target !== '/') {
              navigate(target, { replace: true });
            }
          } catch {
            // Ignore malformed URLs.
          }
        });
        removeListener = handle?.remove?.bind(handle);
      } catch {
        // Plugin missing on web build — safe to ignore.
      }
    })();

    return () => {
      try { removeListener && removeListener(); } catch {}
    };
  }, [navigate]);

  return null;
}
